// purpose: iPhone向けR2アトラスを共有／ステージ専用bundleへ分け、先読みと安全な解放を一枚窓化する。

import { Assets, type Spritesheet, type UnresolvedAsset } from 'pixi.js';
import { STAGES, StageCueKind } from '../../../data/stages';
import type { R2Index } from '../../layerstack/LayerStackLoader';
import { FX_ONE_SHOT_SLUGS } from '../fx/FxDirector';
import { stageBandSpecs } from '../stage/bands';
import {
  BOSS_SLUGS,
  ENEMY_SLUGS,
  GATE_SLUG,
  NODE_LENS_SLUGS,
  NODE_SLUG,
  PICKUP_SLUG,
  PLAYER_SLUG,
  PROJECTILE_SLUGS,
  rtPath,
} from './registry';

const SHARED_BUNDLE = 'r2-mobile-shared';
const STAGE_BUNDLE_PREFIX = 'r2-mobile-stage-';
let registered = false;
const stageAtlasUrls = new Map<number, readonly string[]>();

export const MOBILE_SHARED_ENTITY_SLUGS: readonly string[] = Object.freeze([
  PLAYER_SLUG,
  NODE_SLUG,
  PICKUP_SLUG,
  GATE_SLUG,
  ...PROJECTILE_SLUGS.filter((slug) => slug !== ''),
  ...NODE_LENS_SLUGS,
]);

function stageBundleName(stageId: number): string {
  return `${STAGE_BUNDLE_PREFIX}${stageId}`;
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value !== ''))];
}

function atlasDirectory(rungPath: string): string {
  return rungPath.endsWith('/') ? rungPath : `${rungPath}/`;
}

function atlasUrl(rungPath: string): string {
  return `${atlasDirectory(rungPath)}atlas.json`;
}

function atlasDescriptor(rungPath: string): UnresolvedAsset {
  const directory = atlasDirectory(rungPath);
  return {
    alias: `r2-atlas:${directory}`,
    src: `${directory}atlas.json`,
    data: {
      textureOptions: { scaleMode: 'nearest' },
    },
  };
}

export function mobileStageSlugs(stageId: number): string[] {
  const stage = STAGES[stageId];
  if (!stage) return [];
  const slugs: string[] = [];
  for (const cue of stage.cues) {
    if (cue.kind === StageCueKind.SpawnWave) {
      const slug = ENEMY_SLUGS[cue.a];
      if (slug) slugs.push(slug);
    } else if (cue.kind === StageCueKind.MidBoss || cue.kind === StageCueKind.FinalBoss) {
      const slug = BOSS_SLUGS[cue.a];
      if (slug) slugs.push(slug);
    }
  }
  return unique(slugs);
}

const stageUseCounts = new Map<string, number>();
for (const stage of STAGES) {
  for (const slug of mobileStageSlugs(stage.id)) {
    stageUseCounts.set(slug, (stageUseCounts.get(slug) ?? 0) + 1);
  }
}

/**
 * Pixi 8.19 unloadBundle destroys every resolved source in that bundle rather
 * than reference-counting overlapping bundles. Assets used by multiple stages
 * therefore live in shared; stage bundles contain exclusive atlases only.
 */
export const MOBILE_RETAINED_STAGE_SLUGS: readonly string[] = Object.freeze(
  [...stageUseCounts.entries()]
    .filter(([, count]) => count > 1)
    .map(([slug]) => slug)
    .sort(),
);

function mobileExclusiveStageSlugs(stageId: number): string[] {
  return mobileStageSlugs(stageId).filter((slug) => (stageUseCounts.get(slug) ?? 0) === 1);
}

/** Register once globally; unloadable stage bundles never overlap shared sources. */
export function registerMobileR2Bundles(index: R2Index): void {
  if (registered) return;
  const sharedPaths = [
    ...stageBandSpecs(index).map((spec) => spec.rungPath),
    ...unique([
      ...MOBILE_SHARED_ENTITY_SLUGS,
      ...MOBILE_RETAINED_STAGE_SLUGS,
      ...FX_ONE_SHOT_SLUGS,
    ]).map((slug) => rtPath(index, slug)),
  ];
  Assets.addBundle(SHARED_BUNDLE, unique(sharedPaths).map(atlasDescriptor));
  for (const stage of STAGES) {
    const exclusivePaths = mobileExclusiveStageSlugs(stage.id).map((slug) => rtPath(index, slug));
    stageAtlasUrls.set(stage.id, exclusivePaths.map(atlasUrl));
    Assets.addBundle(
      stageBundleName(stage.id),
      exclusivePaths.map(atlasDescriptor),
    );
  }
  registered = true;
}

function linkedAtlasUrls(rootAtlasUrl: string): string[] {
  const resolvedRootUrl = Assets.resolver.resolve(rootAtlasUrl).src;
  if (!Assets.cache.has(resolvedRootUrl)) return [];

  const rootSheet = Assets.cache.get(resolvedRootUrl) as Spritesheet;
  const related = rootSheet.data?.meta?.related_multi_packs;
  if (!Array.isArray(related) || related.length === 0) return [];

  // Return unresolved URLs so Assets applies basePath/defaultSearchParams once,
  // exactly as it did when the multipack loader fetched these pages.
  const queryIndex = rootAtlasUrl.indexOf('?');
  const rootPath = queryIndex < 0 ? rootAtlasUrl : rootAtlasUrl.slice(0, queryIndex);
  const search = queryIndex < 0 ? '' : rootAtlasUrl.slice(queryIndex + 1);
  const slashIndex = rootPath.lastIndexOf('/');
  const directory = slashIndex < 0 ? '' : rootPath.slice(0, slashIndex + 1);
  return unique(
    related
      .filter((item): item is string => typeof item === 'string' && item !== '')
      .map((item) => `${directory}${item}${search === '' ? '' : `?${search}`}`),
  );
}

async function unloadStageMultipackPages(stageId: number): Promise<void> {
  const roots = stageAtlasUrls.get(stageId) ?? [];
  const linked = unique(roots.flatMap(linkedAtlasUrls));
  if (linked.length === 0) return;

  // Pixi's root spritesheet unloader only releases the root texture source.
  // Loader.unload invokes each linked sheet's spritesheet unloader (and its
  // atlas image unloader); unloadBundle then clears the root-owned cache keys.
  // Going through Assets.unload here would warn because nested multipack URLs
  // are cache values, but are not independent top-level Cache registrations.
  await Assets.loader.unload(linked.map((url) => Assets.resolver.resolve(url)));
}

export async function loadMobileR2Base(): Promise<void> {
  await Assets.loadBundle([SHARED_BUNDLE, stageBundleName(0)]);
}

export async function loadMobileR2Stage(stageId: number): Promise<void> {
  if (!STAGES[stageId]) return;
  await Assets.loadBundle(stageBundleName(stageId));
}

export async function unloadMobileR2Stage(stageId: number): Promise<void> {
  if (!STAGES[stageId]) return;
  try {
    await unloadStageMultipackPages(stageId);
  } finally {
    await Assets.unloadBundle(stageBundleName(stageId));
  }
}
